/**
 * Permission actions that can be performed on resources
 */
export type PermissionAction =
  | 'view'
  | 'create'
  | 'update'
  | 'delete'
  | 'transfer'
  | 'settle'
  | 'cancel'
  | 'approve'
  | 'export';

/**
 * Permission resource domains in the admin application
 */
export type PermissionResource =
  | 'transaction'
  | 'establishment'
  | 'user'
  | 'role'
  | 'report'
  | 'settings'
  | 'audit';

/**
 * Permission string format: "resource:action"
 * @example "transaction:cancel", "user:create", "report:export"
 */
export type Permission = `${PermissionResource}:${PermissionAction}`;

/**
 * Result of a permission check with optional denial reason
 */
export interface PermissionCheckResult {
  allowed: boolean;
  reason?: string;
}

/**
 * All possible permissions grouped by resource
 * Used for role-permission matrix and UI rendering
 */
export const ALL_PERMISSIONS: Record<PermissionResource, PermissionAction[]> = {
  transaction: ['view', 'create', 'update', 'delete', 'transfer', 'settle', 'cancel', 'approve'],
  establishment: ['view', 'create', 'update', 'delete'],
  user: ['view', 'create', 'update', 'delete'],
  role: ['view', 'create', 'update', 'delete'],
  report: ['view', 'export'],
  settings: ['view', 'update'],
  audit: ['view', 'export'],
};

/**
 * Default permissions for SuperAdmin role - has all permissions
 */
export const SUPERADMIN_PERMISSIONS: Permission[] = Object.entries(ALL_PERMISSIONS).flatMap(
  ([resource, actions]) => actions.map((action) => `${resource}:${action}` as Permission),
);

/**
 * Default permissions for Admin role - all except role management
 */
export const ADMIN_PERMISSIONS: Permission[] = SUPERADMIN_PERMISSIONS.filter(
  (p) => !p.startsWith('role:') || p === 'role:view',
);
